import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import bcrypt from 'bcryptjs';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  Eye,
  EyeOff,
  Shield,
  Globe,
  Laptop,
  Wifi,
  Clock,
  Monitor,
  Languages,
  MapPin,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Sun,
  Moon,
  ArrowLeft,
  History,
  Search,
  ArrowUpDown,
  Trash2,
  Filter
} from 'lucide-react';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const AccountSettings = () => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [errors, setErrors] = useState({});
  const [loginHistory, setLoginHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'login_timestamp',
    direction: 'desc'
  });
  const [filterConfig, setFilterConfig] = useState({
    status: 'all', // 'all', 'success', 'failed'
    date: 'all', // 'all', 'today', 'week', 'month'
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  // Kullanıcı profili ve cihaz bilgilerini getir
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;
        if (!session) return;

        // Kullanıcı profilini getir
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', session.user.email)
          .single();

        if (profileError) throw profileError;
        setUserProfile(profile);

        // Cihaz bilgilerini getir
        const { data: deviceData, error: deviceError } = await supabase
          .from('verification_codes')
          .select('device_info')
          .eq('email', session.user.email)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (deviceError) throw deviceError;
        setDeviceInfo(deviceData.device_info);
      } catch (error) {
        console.error('Data fetch error:', error);
        toast.error('Bilgiler alınamadı');
      }
    };

    fetchUserData();
  }, []);

  // Giriş geçmişini getir (ilk kez)
  useEffect(() => {
    const fetchLoginHistory = async () => {
      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;
        if (!session) return;

        const { data: history, error: historyError } = await supabase
          .from('user_login_history')
          .select('*')
          .eq('user_id', session.user.id)
          .order('login_timestamp', { ascending: false });

        if (historyError) throw historyError;

        setLoginHistory(history || []);
      } catch (error) {
        console.error('Login history fetch error:', error);
        toast.error('Giriş geçmişi alınamadı');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchLoginHistory();
  }, []);

  // Realtime Subscription - Giriş Geçmişi
  useEffect(() => {
    let subscription;
    const initRealtimeSubscription = async () => {
      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        return;
      }
      if (!session) return;

      // Kullanıcının user_id'sini alıyoruz
      const userId = session.user.id;

      // Realtime kanalı oluşturuyoruz:
      subscription = supabase
        .channel('realtime-login-history')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_login_history',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            // payload içinde 'INSERT', 'UPDATE' vb. eventType olabilir.
            // Örnek olarak sadece yeni girişler (INSERT) handle edelim:
            if (payload.eventType === 'INSERT') {
              const newRecord = payload.new;
              // En yeni kayıtları en üstte göstermek için, dizi başına ekleyebilirsiniz
              setLoginHistory((prev) => [newRecord, ...prev]);
            }
            // Eğer UPDATE, DELETE gibi senaryolar da desteklenecekse, 
            // ona göre ek kontroller yapabilirsiniz.
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
           // console.log('Realtime subscription ready!');
          }
        });
    };

    initRealtimeSubscription();

    // Bileşen unmount olduğunda subscription iptali
    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, []);

  // Sıralama fonksiyonu
  const sortData = (data) => {
    return [...data].sort((a, b) => {
      if (sortConfig.key === 'login_timestamp') {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filtreleme fonksiyonu
  const filterData = (data) => {
    return data.filter(entry => {
      // Durum filtresi
      if (filterConfig.status !== 'all') {
        if (filterConfig.status === 'success' && !entry.success) return false;
        if (filterConfig.status === 'failed' && entry.success) return false;
      }

      // Tarih filtresi
      const entryDate = new Date(entry.login_timestamp);
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      if (filterConfig.date === 'today') {
        return entryDate.toDateString() === today.toDateString();
      }
      if (filterConfig.date === 'week') {
        return entryDate >= weekAgo;
      }
      if (filterConfig.date === 'month') {
        return entryDate >= monthAgo;
      }

      return true;
    });
  };

  // Geçmişi temizleme fonksiyonu
  const handleClearHistory = async () => {
    if (window.confirm('Tüm giriş geçmişiniz silinecek. Emin misiniz?')) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { error } = await supabase
          .from('user_login_history')
          .delete()
          .eq('user_id', session.user.id);

        if (error) throw error;

        setLoginHistory([]);
        toast.success('Giriş geçmişi başarıyla temizlendi');
      } catch (error) {
        console.error('Clear history error:', error);
        toast.error('Geçmiş temizlenirken bir hata oluştu');
      }
    }
  };

  // Filtrelenmiş ve sıralanmış veriyi hazırla
  const processedHistory = sortData(filterData(loginHistory)).filter(entry =>
    entry.ip_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    new Date(entry.login_timestamp).toLocaleString('tr-TR').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Şifre değiştirme işlemi
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErrors({});
  
    // Validasyonlar
    if (!passwordData.currentPassword) {
      setErrors((prev) => ({ ...prev, currentPassword: 'Mevcut şifre gerekli' }));
      return;
    }
    if (!passwordData.newPassword) {
      setErrors((prev) => ({ ...prev, newPassword: 'Yeni şifre gerekli' }));
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: 'Şifreler eşleşmiyor' }));
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setErrors((prev) => ({
        ...prev,
        newPassword: 'Şifre en az 8 karakter olmalı',
      }));
      return;
    }
  
    setIsLoading(true);
    try {
      // Mevcut şifreyi doğrula
      const {
        data: { user },
        error: signInError,
      } = await supabase.auth.signInWithPassword({
        email: userProfile.email,
        password: passwordData.currentPassword,
      });
      if (signInError) {
        setErrors((prev) => ({
          ...prev,
          currentPassword: 'Mevcut şifre yanlış',
        }));
        throw signInError;
      }
  
      // Yeni şifreyi hashle
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(passwordData.newPassword, salt);
  
      // Şifreyi güncelle
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });
      if (updateError) throw updateError;
  
      // Veritabanında hash'i güncelle
      const { error: dbError } = await supabase
        .from('user_profiles')
        .update({
          password_hash: hashedPassword,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (dbError) throw dbError;
  
      // "security_settings" tablosunda son şifre değiştirilme tarihini güncelle
      const { error: securityError } = await supabase
        .from('security_settings')
        .update({
          last_password_change: new Date().toISOString(),
        })
        .eq('user_id', user.id);
      if (securityError) throw securityError;
  
      toast.success('Şifreniz başarıyla güncellendi');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.message || 'Şifre güncellenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Navbar */}
      <div className="sticky top-0 z-10 bg-white/80 border-gray-200 backdrop-blur-xl border-b dark:bg-gray-800/80 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1
              className={`text-xl font-semibold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}
            >
              Hesap Ayarları
            </h1>
            <div className="flex items-center gap-4">
              {/* Tema Değiştirme */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className={`
                  p-2 rounded-full
                  ${
                    isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  transition-colors
                `}
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </motion.button>

              {/* Geri Dön */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(-1)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg
                  ${
                    isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                  transition-colors
                `}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Geri Dön</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Sol Kolon - Şifre Değiştirme */}
          <div
            className={`${
              isDark
                ? 'bg-gray-800/50 border-gray-700'
                : 'bg-white border-gray-200'
            } rounded-2xl border shadow-lg backdrop-blur-sm`}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`p-3 rounded-xl ${
                    isDark ? 'bg-blue-500/10' : 'bg-blue-50'
                  }`}
                >
                  <Lock
                    className={`w-6 h-6 ${
                      isDark ? 'text-blue-400' : 'text-blue-600'
                    }`}
                  />
                </div>
                <div>
                  <h2
                    className={`text-xl font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Şifre Değiştir
                  </h2>
                  <p
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Hesap güvenliğiniz için şifrenizi düzenli olarak değiştirin
                  </p>
                </div>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-6">
                {/* Mevcut Şifre */}
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    } mb-2`}
                  >
                    Mevcut Şifre
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          currentPassword: e.target.value,
                        })
                      }
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark
                          ? 'bg-white/5 border-white/10 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                      } border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.currentPassword ? 'border-red-500' : ''
                      }`}
                      placeholder="••••••••"
                    />
                    <Lock
                      className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                    >
                      {showCurrentPassword ? (
                        <EyeOff
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      ) : (
                        <Eye
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      )}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.currentPassword}
                    </p>
                  )}
                </div>

                {/* Yeni Şifre */}
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    } mb-2`}
                  >
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark
                          ? 'bg-white/5 border-white/10 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                      } border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.newPassword ? 'border-red-500' : ''
                      }`}
                      placeholder="••••••••"
                    />
                    <Shield
                      className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                    >
                      {showNewPassword ? (
                        <EyeOff
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      ) : (
                        <Eye
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      )}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                {/* Şifre Tekrar */}
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    } mb-2`}
                  >
                    Yeni Şifre (Tekrar)
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      className={`w-full pl-12 pr-12 py-3 ${
                        isDark
                          ? 'bg-white/5 border-white/10 text-white'
                          : 'bg-white border-gray-200 text-gray-900'
                      } border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                        errors.confirmPassword ? 'border-red-500' : ''
                      }`}
                      placeholder="••••••••"
                    />
                    <Shield
                      className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2"
                    >
                      {showConfirmPassword ? (
                        <EyeOff
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      ) : (
                        <Eye
                          className={`w-5 h-5 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white ${
                    isLoading
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } transition-colors`}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Güncelleniyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Şifreyi Güncelle
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </div>

          {/* Sağ Kolon - Hesap Bilgileri */}
          <div
            className={`${
              isDark
                ? 'bg-gray-800/50 border-gray-700'
                : 'bg-white border-gray-200'
            } rounded-2xl border shadow-lg backdrop-blur-sm`}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`p-3 rounded-xl ${
                    isDark ? 'bg-green-500/10' : 'bg-green-50'
                  }`}
                >
                  <Globe
                    className={`w-6 h-6 ${
                      isDark ? 'text-green-400' : 'text-green-600'
                    }`}
                  />
                </div>
                <div>
                  <h2
                    className={`text-xl font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Hesap Bilgileri
                  </h2>
                  <p
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Cihaz ve bağlantı detayları
                  </p>
                </div>
              </div>

              {deviceInfo ? (
                <div className="space-y-6">
                  {/* Konum Bilgileri */}
                  <div className="space-y-3">
                    <h3
                      className={`text-sm font-medium ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Konum Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <MapPin
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              Adres
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.address}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Globe
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              Ülke / Saat Dilimi
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.country} / {deviceInfo.time_zone}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cihaz Bilgileri */}
                  <div className="space-y-3">
                    <h3
                      className={`text-sm font-medium ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Cihaz Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Laptop
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              Cihaz / Tip
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.device_name} / {deviceInfo.device_type}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Monitor
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              Ekran
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.screen_resolution} (
                              {deviceInfo.screen_orientation})
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bağlantı Bilgileri */}
                  <div className="space-y-3">
                    <h3
                      className={`text-sm font-medium ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Bağlantı Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Wifi
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              IP / ISP
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.ip_address} / {deviceInfo.isp_name}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-xl ${
                          isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Languages
                            className={`w-5 h-5 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          />
                          <div>
                            <p
                              className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              Tarayıcı / Dil
                            </p>
                            <p
                              className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}
                            >
                              {deviceInfo.web_browser_name}{' '}
                              {deviceInfo.browser_version} /{' '}
                              {deviceInfo.browser_language}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Son Giriş */}
                  <div
                    className={`p-4 rounded-xl ${
                      isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock
                        className={`w-5 h-5 ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      />
                      <div>
                        <p
                          className={`text-xs ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          Son Giriş Tarihi
                        </p>
                        <p
                          className={`text-sm font-medium ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {deviceInfo.creation_date}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`p-8 text-center ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
                  <p>Bilgiler yükleniyor...</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Giriş Geçmişi Tablosu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`mt-8 ${
            isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
          } rounded-2xl border shadow-lg backdrop-blur-sm`}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-xl ${
                    isDark ? 'bg-purple-500/10' : 'bg-purple-50'
                  }`}
                >
                  <History
                    className={`w-6 h-6 ${
                      isDark ? 'text-purple-400' : 'text-purple-600'
                    }`}
                  />
                </div>
                <div>
                  <h2
                    className={`text-xl font-semibold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Giriş Geçmişi
                  </h2>
                  <p
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    Son oturum açma kayıtları
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Arama */}
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="IP veya konum ara..."
                    className={`pl-10 pr-4 py-2 ${
                      isDark
                        ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                    } border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  />
                </div>

                {/* Filtre Butonu */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className={`p-2 rounded-lg ${
                      isDark
                        ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <Filter className="w-5 h-5" />
                  </button>

                  {/* Filtre Menüsü */}
                  {showFilterMenu && (
                    <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg ${
                      isDark
                        ? 'bg-gray-800 border border-gray-700'
                        : 'bg-white border border-gray-200'
                    } z-10`}>
                      <div className="p-3">
                        <div className="mb-3">
                          <label className={`block text-sm font-medium mb-1 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Durum
                          </label>
                          <select
                            value={filterConfig.status}
                            onChange={(e) => setFilterConfig(prev => ({ ...prev, status: e.target.value }))}
                            className={`w-full rounded-lg text-sm ${
                              isDark
                                ? 'bg-gray-700 border-gray-600 text-gray-300'
                                : 'bg-gray-50 border-gray-200 text-gray-900'
                            }`}
                          >
                            <option value="all">Tümü</option>
                            <option value="success">Başarılı</option>
                            <option value="failed">Başarısız</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Tarih
                          </label>
                          <select
                            value={filterConfig.date}
                            onChange={(e) => setFilterConfig(prev => ({ ...prev, date: e.target.value }))}
                            className={`w-full rounded-lg text-sm ${
                              isDark
                                ? 'bg-gray-700 border-gray-600 text-gray-300'
                                : 'bg-gray-50 border-gray-200 text-gray-900'
                            }`}
                          >
                            <option value="all">Tümü</option>
                            <option value="today">Bugün</option>
                            <option value="week">Son 7 Gün</option>
                            <option value="month">Son 30 Gün</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Geçmişi Temizle */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClearHistory}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                    isDark
                      ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  } transition-colors`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Geçmişi Temizle</span>
                </motion.button>
              </div>
            </div>

            {/* Tablo */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr
                    className={`text-left ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => setSortConfig({
                          key: 'login_timestamp',
                          direction: sortConfig.key === 'login_timestamp' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        })}
                        className="flex items-center gap-2 hover:text-purple-500 transition-colors"
                      >
                        Tarih/Saat
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => setSortConfig({
                          key: 'ip_address',
                          direction: sortConfig.key === 'ip_address' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        })}
                        className="flex items-center gap-2 hover:text-purple-500 transition-colors"
                      >
                        IP Adresi
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => setSortConfig({
                          key: 'location',
                          direction: sortConfig.key === 'location' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        })}
                        className="flex items-center gap-2 hover:text-purple-500 transition-colors"
                      >
                        Konum
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </th>
                    <th className="px-4 py-3 font-medium">Cihaz</th>
                    <th className="px-4 py-3 font-medium">
                      <button
                        onClick={() => setSortConfig({
                          key: 'success',
                          direction: sortConfig.key === 'success' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        })}
                        className="flex items-center gap-2 hover:text-purple-500 transition-colors"
                      >
                        Durum
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {isLoadingHistory ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-purple-500" />
                        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          Giriş geçmişi yükleniyor...
                        </p>
                      </td>
                    </tr>
                  ) : processedHistory.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center animate-pulse">
                        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          {searchQuery
                            ? 'Aranan kriterlere uygun giriş kaydı bulunamadı'
                            : 'Henüz giriş kaydı bulunmuyor'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    processedHistory.map((entry) => (
                      <tr
                        key={entry.id}
                        className={`${
                          isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                        } transition-colors`}
                      >
                        <td
                          className={`px-4 py-3 ${
                            isDark ? 'text-gray-300' : 'text-gray-900'
                          }`}
                        >
                          {new Date(entry.login_timestamp).toLocaleString('tr-TR')}
                        </td>
                        <td
                          className={`px-4 py-3 font-mono text-sm ${
                            isDark ? 'text-gray-300' : 'text-gray-900'
                          }`}
                        >
                          {entry.ip_address}
                        </td>
                        <td
                          className={`px-4 py-3 ${
                            isDark ? 'text-gray-300' : 'text-gray-900'
                          }`}
                        >
                          {entry.location || 'Bilinmiyor'}
                        </td>
                        <td
                          className={`px-4 py-3 ${
                            isDark ? 'text-gray-300' : 'text-gray-900'
                          }`}
                        >
                          {entry.device_info ? (
                            <div className="text-sm">
                              <div className="font-medium">
                                {entry.device_info.deviceType || 'Bilinmiyor'}
                              </div>
                              <div className="text-xs opacity-75">
                                {entry.device_info.browser} - {entry.device_info.os}
                              </div>
                            </div>
                          ) : (
                            'Bilinmiyor'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.success
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {entry.success ? 'Başarılı' : 'Başarısız'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountSettings;
